import { http, HttpResponse, delay } from 'msw';

import {
  mockInitialOrgSoftwareApps,
  mockMdmInformation,
  mockProvisionStatus,
  mockRecoveryKey,
  mockSoftwareCatalogApps,
  mockSystemWithPolicyStats,
} from '@/mocks/data/mdmData';
import { OrgSoftwareApp, SoftwareOsType } from '@/types/mdmManagement';

// Light in-memory state so adding/removing a software app is reflected in the
// follow-up list and catalog fetches.
const orgSoftwareApps: OrgSoftwareApp[] = [...mockInitialOrgSoftwareApps];

// MDM management service (VITE_MDM_MANAGEMENT_BASE_URL, which ends in /v1). Paths omit `/v1` so they also match in Storybook, where `window._env_` is not defined and the base URL is empty.
export const mdmManagementHandlers = [
  http.get('*/jumpcloud/provision/:organizationId', async () => {
    await delay();
    return HttpResponse.json(mockProvisionStatus, { status: 200 });
  }),

  http.get('*/jumpcloud/:organizationId/mdm-information', async () => {
    await delay();
    return HttpResponse.json(mockMdmInformation, { status: 200 });
  }),

  http.get('*/jumpcloud/:organizationId/darwin/download-link', async () => {
    await delay();
    return HttpResponse.json('https://example.com/electric-agent.pkg', {
      status: 200,
    });
  }),

  http.put('*/jumpcloud/:organizationId/applemdms', async () => {
    await delay();
    return new HttpResponse(null, { status: 204 });
  }),

  http.get('*/jumpcloud/:organizationId/applemdms/csr', async () => {
    await delay();
    return new HttpResponse('mock-apple-csr', {
      status: 200,
      headers: { 'Content-Type': 'application/octet-stream' },
    });
  }),

  http.post('*/jumpcloud/:organizationId/device/:deviceId/lock', async () => {
    await delay();
    return new HttpResponse(null, { status: 204 });
  }),

  http.post('*/jumpcloud/:organizationId/device/:deviceId/wipe', async () => {
    await delay();
    return new HttpResponse(null, { status: 204 });
  }),

  http.post(
    '*/jumpcloud/:organizationId/device/:deviceId/unenroll',
    async () => {
      await delay();
      return new HttpResponse(null, { status: 204 });
    }
  ),

  http.get(
    '*/jumpcloud/:organizationId/serialnumber/:serialNumber/recovery-key',
    async () => {
      await delay();
      return HttpResponse.json(
        { recovery_key: mockRecoveryKey },
        { status: 200 }
      );
    }
  ),

  http.get(
    '*/jumpcloud/:organizationId/systemsbyserial/:serialNumber',
    async ({ params }) => {
      await delay();
      return HttpResponse.json(
        mockSystemWithPolicyStats(params.serialNumber as string),
        { status: 200 }
      );
    }
  ),

  http.delete(
    '*/jumpcloud/:organizationId/systemsbyserial/:serialNumber',
    async () => {
      await delay();
      return new HttpResponse(null, { status: 204 });
    }
  ),

  http.get(
    '*/jumpcloud/:organizationId/software-catalog/apps',
    async ({ request }) => {
      const url = new URL(request.url);
      const osType = url.searchParams.get('os_type');
      const query = url.searchParams.get('query')?.toLowerCase();

      const results = mockSoftwareCatalogApps
        .filter(app => !osType || app.os_type === osType)
        .filter(app => !query || app.app_name.toLowerCase().includes(query))
        .map(app => ({
          ...app,
          is_available: !orgSoftwareApps.some(
            orgApp => orgApp.software_app_id === app.software_app_id
          ),
        }));

      await delay();
      return HttpResponse.json(results, { status: 200 });
    }
  ),

  http.get('*/jumpcloud/:organizationId/software-apps', async () => {
    await delay();
    return HttpResponse.json(orgSoftwareApps, { status: 200 });
  }),

  http.post(
    '*/jumpcloud/:organizationId/software-apps',
    async ({ request }) => {
      const { software_app_id, os_type } = (await request.json()) as {
        software_app_id: string;
        os_type: SoftwareOsType;
      };
      const catalogApp = mockSoftwareCatalogApps.find(
        app => app.software_app_id === software_app_id
      );
      if (!catalogApp) {
        return new HttpResponse(null, { status: 404 });
      }
      orgSoftwareApps.push({
        software_app_id: catalogApp.software_app_id,
        app_name: catalogApp.app_name,
        os_type,
        description: catalogApp.description,
        icon_url: catalogApp.icon_url,
      });
      await delay();
      return new HttpResponse(null, { status: 204 });
    }
  ),

  http.delete(
    '*/jumpcloud/:organizationId/software-apps/:softwareAppId',
    async ({ params }) => {
      const index = orgSoftwareApps.findIndex(
        app => app.software_app_id === params.softwareAppId
      );
      if (index === -1) {
        return new HttpResponse(null, { status: 404 });
      }
      orgSoftwareApps.splice(index, 1);
      await delay();
      return new HttpResponse(null, { status: 204 });
    }
  ),
];
